import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Briefcase,
  CalendarDays,
  Camera,
  Clock,
  GraduationCap,
  Linkedin,
  LogIn,
  LogOut,
  MapPin,
  Plus,
  Save,
  Search,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";
import {
  addWorkHistory,
  alumniDirectory,
  alumniForgotPassword,
  alumniLogin,
  alumniLogout,
  alumniResetPassword,
  alumniSignup,
  deleteWorkHistory,
  errorMessage,
  fetchAlumniEvents,
  fetchAlumniStats,
  fetchFeaturedAlumni,
  getAlumniProfile,
  updateAlumniProfile,
} from "../lib/api";
import { cn, formatDate, parseDate } from "../lib/site";
import useSession from "../lib/useSession";
import { Alert, Button, Card, Checkbox, EmptyState, Field, FilePicker, PageLoader, TextField } from "../ui";
import { AuthLayout } from "../portal/layouts";
import { ForgotPasswordPage, PasswordInput, ResetPasswordPage } from "../portal/AuthPages";
import heroImage from "../assets/optimized/hero-4.jpg";

const THIS_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: THIS_YEAR - 1990 + 2 }, (_, i) => THIS_YEAR + 1 - i);
const PROGRAMMES = ["IB Diploma Programme", "IB MYP", "IB PYP", "Matriculation", "O / A Levels", "Other"];

function Avatar({ src, name, className }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return <Initials name={name} className={className} />;
  return <img src={src} alt="" data-keep-broken="1" onError={() => setFailed(true)} className={cn("object-cover", className)} />;
}

function Initials({ name, className }) {
  const initials = (name || "?")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return <div className={cn("grid place-items-center bg-navy-100 font-display font-bold text-navy-700", className)}>{initials}</div>;
}

// Public landing ----------------------------------------------------------------------

export function AlumniLanding() {
  const auth = useSession("alumni");
  const [featured, setFeatured] = useState([]);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchFeaturedAlumni().then(setFeatured).catch(() => {});
    fetchAlumniEvents().then(setEvents).catch(() => {});
    fetchAlumniStats().then(setStats).catch(() => {});
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = events.filter((e) => e.date >= today);

  return (
    <>
      <section className="relative isolate overflow-hidden bg-navy-900 text-white">
        <img src={heroImage} alt="" className="absolute inset-0 -z-10 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-navy-950 via-navy-900/90 to-navy-800/40" />
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28">
          <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-sm font-semibold text-gold-300 ring-1 ring-white/20">
            <GraduationCap className="h-4 w-4" aria-hidden /> TIS Alumni Network
          </p>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold leading-tight sm:text-5xl md:text-6xl">
            Stay connected. <span className="text-gold-400">Grow together.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-navy-100">
            Reconnect with classmates, share your journey since TIS and open doors for the students who follow you.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {auth?.access ? (
              <Button to="/alumni/dashboard" variant="gold" size="lg" icon={Users}>
                Open the alumni directory
              </Button>
            ) : (
              <>
                <Button to="/alumni/signup" variant="gold" size="lg" icon={UserPlus}>
                  Join the network
                </Button>
                <Button to="/alumni/login" variant="light" size="lg" icon={LogIn}>
                  Alumni sign in
                </Button>
              </>
            )}
          </div>
          {stats && stats.members > 0 && (
            <dl className="mt-12 grid max-w-lg grid-cols-2 gap-6">
              <div>
                <dt className="text-sm text-navy-200">Registered alumni</dt>
                <dd className="font-display text-3xl font-extrabold">{stats.members}</dd>
              </div>
              <div>
                <dt className="text-sm text-navy-200">Graduating classes</dt>
                <dd className="font-display text-3xl font-extrabold">{stats.batches}</dd>
              </div>
            </dl>
          )}
        </div>
      </section>

      {featured.length > 0 && (
        <section className="bg-white py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <SectionHeading eyebrow="Alumni spotlight" title="Where our graduates are today" />
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {featured.map((a) => (
                <article key={a.id} className="flex flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-card transition hover:shadow-lift">
                  <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                    <Avatar src={a.profile_image} name={a.name} className="h-full w-full object-top text-5xl" />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-display text-xl font-bold text-navy-800">{a.name}</h3>
                    <p className="text-sm font-semibold text-gold-600">
                      {[a.program, a.batch && `Class of ${a.batch}`].filter(Boolean).join(" · ")}
                    </p>
                    {a.tagline && <p className="mt-3 text-slate-700">{a.tagline}</p>}
                    {a.description && <p className="mt-2 line-clamp-4 text-sm text-slate-600">{a.description}</p>}
                    {a.tags && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {a.tags.split(",").filter((t) => t.trim()).map((t) => (
                          <span key={t} className="rounded-full bg-navy-50 px-2.5 py-1 text-xs font-semibold text-navy-700">
                            {t.trim()}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-slate-50 py-16 md:py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-2">
          <div>
            <SectionHeading eyebrow="Get together" title="Alumni events" align="left" />
            <div className="mt-8 space-y-4">
              {upcoming.length === 0 && (
                <p className="rounded-2xl bg-white p-6 text-slate-600 ring-1 ring-slate-200">
                  No events are scheduled right now. Join the network to hear about the next reunion first.
                </p>
              )}
              {upcoming.map((e) => (
                <div key={e.id} className="flex gap-4 rounded-2xl bg-white p-5 ring-1 ring-slate-200">
                  <div className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-navy-700 text-center text-white">
                    <div>
                      <div className="font-display text-2xl font-extrabold leading-none">{parseDate(e.date)?.getDate()}</div>
                      <div className="text-xs font-semibold uppercase text-gold-300">
                        {parseDate(e.date)?.toLocaleString("en-GB", { month: "short" })}
                      </div>
                    </div>
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-display text-lg font-bold text-navy-800">{e.title}</h3>
                    <p className="mt-1 flex flex-wrap items-center gap-3 text-sm text-slate-500">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-4 w-4" aria-hidden /> {e.time?.slice(0, 5)}
                      </span>
                      {e.location && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-4 w-4" aria-hidden /> {e.location}
                        </span>
                      )}
                    </p>
                    {e.description && <p className="mt-2 text-sm text-slate-700">{e.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-3xl bg-navy-800 p-8 text-white sm:p-10">
            <h2 className="font-display text-3xl font-extrabold">Why join?</h2>
            <ul className="mt-6 space-y-5">
              {[
                [Users, "Find your classmates", "Search the directory by batch, university, city or career."],
                [Briefcase, "Mentor and be mentored", "Share your career path and help current TIS students decide theirs."],
                [CalendarDays, "Reunions and events", "Be first to know about homecomings, talks and batch reunions."],
              ].map(([Icon, title, text]) => (
                <li key={title} className="flex gap-4">
                  <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-white/10 text-gold-400">
                    <Icon className="h-5 w-5" aria-hidden />
                  </div>
                  <div>
                    <p className="font-bold">{title}</p>
                    <p className="text-sm text-navy-100">{text}</p>
                  </div>
                </li>
              ))}
            </ul>
            {!auth?.access && (
              <Button to="/alumni/signup" variant="gold" size="lg" className="mt-8" icon={UserPlus}>
                Register as an alumnus
              </Button>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

export function SectionHeading({ eyebrow, title, text, align = "center", dark = false }) {
  return (
    <div className={cn(align === "center" ? "mx-auto max-w-3xl text-center" : "max-w-3xl")}>
      {eyebrow && <p className={cn("text-sm font-bold uppercase tracking-[0.18em]", dark ? "text-gold-400" : "text-gold-600")}>{eyebrow}</p>}
      <h2 className={cn("mt-2 font-display text-3xl font-extrabold leading-tight sm:text-4xl", dark ? "text-white" : "text-navy-800")}>{title}</h2>
      {text && <p className={cn("mt-4 text-lg", dark ? "text-navy-100" : "text-slate-600")}>{text}</p>}
    </div>
  );
}

// Auth -------------------------------------------------------------------------------------

export function AlumniLogin() {
  const auth = useSession("alumni");
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  if (auth?.access) return <Navigate to="/alumni/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await alumniLogin(form.email.trim(), form.password);
      navigate("/alumni/dashboard", { replace: true });
    } catch (err) {
      setError({ pending: err?.response?.data?.pending, text: errorMessage(err, "Incorrect email or password.") });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      brand="alumni"
      title="Alumni sign in"
      subtitle="Welcome back to the TIS alumni network."
      backTo="/alumni"
      backLabel="Alumni home"
      footer={
        <>
          Not registered yet?{" "}
          <Link to="/alumni/signup" className="font-semibold text-navy-700 hover:underline">
            Join the network
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-5">
        {error && (
          <Alert tone={error.pending ? "warning" : "error"} title={error.pending ? "Waiting for approval" : undefined}>
            {error.text}
          </Alert>
        )}
        <TextField id="email" label="Email address" type="email" autoComplete="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <PasswordInput id="password" label="Password" autoComplete="current-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        <div className="flex justify-end">
          <Link to="/alumni/forgot-password" className="text-sm font-semibold text-navy-700 hover:underline">
            Forgot password?
          </Link>
        </div>
        <Button type="submit" size="lg" className="w-full" loading={loading} icon={LogIn} disabled={!form.email || !form.password}>
          Sign in
        </Button>
      </form>
    </AuthLayout>
  );
}

const EMPTY_ALUMNI = {
  name: "",
  email: "",
  phone_number: "",
  batch: String(THIS_YEAR - 1),
  program: PROGRAMMES[0],
  grade: "",
  current_role: "",
  university: "",
  city: "",
  linkedin_url: "",
  tags: "",
  password: "",
  confirm: "",
};

export function AlumniSignup() {
  const [form, setForm] = useState(EMPTY_ALUMNI);
  const [work, setWork] = useState([]);
  const [image, setImage] = useState(null);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = "Please enter a valid email.";
    if (form.phone_number.replace(/\D/g, "").length < 10) errs.phone_number = "Please enter a valid number.";
    if (!form.grade.trim()) errs.grade = "Required.";
    if (form.linkedin_url && !/^https?:\/\//.test(form.linkedin_url)) errs.linkedin_url = "Start with https://";
    if (form.password.length < 8) errs.password = "Use at least 8 characters.";
    if (form.confirm !== form.password) errs.confirm = "Passwords do not match.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setLoading(true);
    setError("");
    try {
      const { confirm, ...fields } = form;
      const res = await alumniSignup(
        { ...fields, email: fields.email.trim(), work_histories: work.filter((w) => w.position && w.company) },
        image
      );
      setDone(res.message);
    } catch (err) {
      setError(errorMessage(err, "We could not register you. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <AuthLayout brand="alumni" title="Thank you for registering!" backTo="/alumni" backLabel="Alumni home">
        <Alert tone="success" title="Registration received">
          {done}
        </Alert>
        <Button to="/alumni" variant="secondary" className="mt-6 w-full">
          Back to alumni home
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      brand="alumni"
      title="Join the TIS alumni network"
      subtitle="Our team checks every registration before it goes live, to keep the directory for real TIS alumni."
      backTo="/alumni"
      backLabel="Alumni home"
      footer={
        <>
          Already registered?{" "}
          <Link to="/alumni/login" className="font-semibold text-navy-700 hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-5" noValidate>
        {error && <Alert tone="error">{error}</Alert>}
        <TextField id="name" label="Full name" required value={form.name} onChange={set("name")} error={errors.name} />
        <div className="grid gap-5 sm:grid-cols-2">
          <TextField id="email" label="Email" type="email" required value={form.email} onChange={set("email")} error={errors.email} />
          <TextField id="phone_number" label="Mobile" type="tel" required value={form.phone_number} onChange={set("phone_number")} error={errors.phone_number} />
          <TextField id="batch" as="select" label="Year you left / graduated" required value={form.batch} onChange={set("batch")}>
            {YEARS.map((y) => (
              <option key={y}>{y}</option>
            ))}
          </TextField>
          <TextField id="grade" label="Last grade at TIS" placeholder="e.g. Year 12" required value={form.grade} onChange={set("grade")} error={errors.grade} />
          <TextField id="program" as="select" label="Programme" className="sm:col-span-2" value={form.program} onChange={set("program")}>
            {PROGRAMMES.map((p) => (
              <option key={p}>{p}</option>
            ))}
          </TextField>
          <TextField id="current_role" label="What you do now" placeholder="e.g. Doctor at AKU" value={form.current_role} onChange={set("current_role")} />
          <TextField id="university" label="University" value={form.university} onChange={set("university")} />
          <TextField id="city" label="City / country" value={form.city} onChange={set("city")} />
          <TextField id="linkedin_url" label="LinkedIn profile" placeholder="https://linkedin.com/in/..." value={form.linkedin_url} onChange={set("linkedin_url")} error={errors.linkedin_url} />
        </div>
        <TextField id="tags" label="Interests / skills" hint="Separate with commas, e.g. Medicine, Football, Mentoring" value={form.tags} onChange={set("tags")} />
        <Field label="Work history" hint="Optional. Add your recent roles.">
          <div className="space-y-3">
            {work.map((w, i) => (
              <div key={i} className="grid grid-cols-[5.5rem_1fr_1fr_auto] items-center gap-2">
                <input className="rounded-lg border-slate-300 text-sm" placeholder="Year" value={w.year} onChange={(e) => setWork(work.map((x, j) => (j === i ? { ...x, year: e.target.value } : x)))} />
                <input className="rounded-lg border-slate-300 text-sm" placeholder="Position" value={w.position} onChange={(e) => setWork(work.map((x, j) => (j === i ? { ...x, position: e.target.value } : x)))} />
                <input className="rounded-lg border-slate-300 text-sm" placeholder="Company" value={w.company} onChange={(e) => setWork(work.map((x, j) => (j === i ? { ...x, company: e.target.value } : x)))} />
                <button type="button" onClick={() => setWork(work.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600" aria-label="Remove">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            <Button type="button" size="sm" variant="secondary" icon={Plus} onClick={() => setWork([...work, { year: String(THIS_YEAR), position: "", company: "" }])}>
              Add role
            </Button>
          </div>
        </Field>
        <Field label="Profile photo" hint="Optional, max 5 MB.">
          <div className="flex items-center gap-3">
            <FilePicker accept="image/*" icon={Camera} onFile={setImage}>
              {image ? "Change photo" : "Add photo"}
            </FilePicker>
            {image && <span className="truncate text-sm text-slate-600">{image.name}</span>}
          </div>
        </Field>
        <PasswordInput id="password" label="Password" autoComplete="new-password" hint="At least 8 characters." value={form.password} onChange={set("password")} error={errors.password} />
        <PasswordInput id="confirm" label="Confirm password" autoComplete="new-password" value={form.confirm} onChange={set("confirm")} error={errors.confirm} />
        <Button type="submit" size="lg" className="w-full" loading={loading} icon={UserPlus}>
          Register
        </Button>
      </form>
    </AuthLayout>
  );
}

export const AlumniForgot = () => <ForgotPasswordPage brand="alumni" onRequest={alumniForgotPassword} loginPath="/alumni/login" />;
export const AlumniReset = () => <ResetPasswordPage brand="alumni" onReset={alumniResetPassword} loginPath="/alumni/login" />;

// Dashboard -------------------------------------------------------------------------------

export function AlumniDashboard() {
  const auth = useSession("alumni");
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "profile" ? "profile" : "directory";

  if (!auth?.access) return <Navigate to="/alumni/login" replace />;

  const logout = () => {
    alumniLogout();
    navigate("/alumni");
  };

  return (
    <div className="bg-slate-50">
      <ToastContainer position="top-center" autoClose={4000} />
      <div className="bg-navy-800 text-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-4 py-8 sm:px-6">
          <div>
            <p className="text-sm font-semibold text-gold-300">TIS Alumni Network</p>
            <h1 className="font-display text-3xl font-extrabold">Welcome, {(auth.user?.name || "").split(" ")[0]}</h1>
          </div>
          <div className="flex gap-2">
            {[
              ["directory", "Directory"],
              ["profile", "My profile"],
            ].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setParams(key === "directory" ? {} : { tab: key })}
                className={cn("rounded-xl px-4 py-2 text-sm font-semibold", tab === key ? "bg-white text-navy-800" : "text-white hover:bg-white/10")}
              >
                {label}
              </button>
            ))}
            <button onClick={logout} className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold text-navy-100 hover:bg-white/10">
              <LogOut className="h-4 w-4" aria-hidden /> Sign out
            </button>
          </div>
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">{tab === "directory" ? <Directory /> : <Profile />}</div>
    </div>
  );
}

function Directory() {
  const [query, setQuery] = useState("");
  const [batch, setBatch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      alumniDirectory({ q: query || undefined, batch: batch || undefined, page })
        .then((d) => {
          setData(d);
          setError("");
        })
        .catch((e) => setError(errorMessage(e)));
    }, 250);
    return () => clearTimeout(t);
  }, [query, batch, page]);

  const pages = data ? Math.max(1, Math.ceil(data.count / 24)) : 1;

  return (
    <>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden />
          <input
            className="block w-full rounded-xl border-0 bg-white py-3 pl-10 pr-4 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-navy-600"
            placeholder="Search by name, university, company, city or interest"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
            aria-label="Search alumni"
          />
        </div>
        <select
          className="rounded-xl border-0 bg-white py-3 pl-4 pr-10 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-navy-600"
          value={batch}
          onChange={(e) => {
            setBatch(e.target.value);
            setPage(1);
          }}
          aria-label="Filter by class year"
        >
          <option value="">All classes</option>
          {(data?.batches || []).map((b) => (
            <option key={b} value={b}>
              Class of {b}
            </option>
          ))}
        </select>
      </div>
      {error && <Alert tone="error">{error}</Alert>}
      {!data && !error && <PageLoader />}
      {data && data.results.length === 0 && (
        <EmptyState icon={Users} title="No alumni found">
          Try a different search, or invite your classmates to join.
        </EmptyState>
      )}
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {data?.results.map((a) => (
          <article key={a.id} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-card">
            <div className="flex items-center gap-4">
              <Avatar src={a.image} name={a.name} className="h-16 w-16 shrink-0 rounded-2xl text-xl" />
              <div className="min-w-0">
                <h3 className="truncate font-display text-lg font-bold text-navy-800">{a.name}</h3>
                <p className="text-sm font-semibold text-gold-600">Class of {a.batch}</p>
                <p className="truncate text-xs text-slate-500">{a.program}</p>
              </div>
            </div>
            <div className="mt-4 space-y-1.5 text-sm text-slate-700">
              {a.current_role && (
                <p className="flex items-start gap-2">
                  <Briefcase className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden /> {a.current_role}
                </p>
              )}
              {a.university && (
                <p className="flex items-start gap-2">
                  <GraduationCap className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden /> {a.university}
                </p>
              )}
              {a.city && (
                <p className="flex items-start gap-2">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden /> {a.city}
                </p>
              )}
            </div>
            {a.tags && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {a.tags.split(",").filter((t) => t.trim()).slice(0, 5).map((t) => (
                  <span key={t} className="rounded-full bg-navy-50 px-2 py-0.5 text-xs font-semibold text-navy-700">
                    {t.trim()}
                  </span>
                ))}
              </div>
            )}
            <div className="mt-auto flex flex-wrap gap-3 pt-4 text-sm">
              {a.linkedin_url && (
                <a href={a.linkedin_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-navy-700 hover:underline">
                  <Linkedin className="h-4 w-4" aria-hidden /> LinkedIn
                </a>
              )}
              {a.email && (
                <a href={`mailto:${a.email}`} className="font-semibold text-navy-700 hover:underline">
                  Email
                </a>
              )}
            </div>
          </article>
        ))}
      </div>
      {data && pages > 1 && (
        <div className="mt-8 flex items-center justify-center gap-3">
          <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            Previous
          </Button>
          <span className="text-sm text-slate-600">
            Page {page} of {pages}
          </span>
          <Button variant="secondary" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>
            Next
          </Button>
        </div>
      )}
    </>
  );
}

function Profile() {
  const [profile, setProfile] = useState(null);
  const [image, setImage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [work, setWork] = useState({ year: String(THIS_YEAR), position: "", company: "" });
  const preview = useMemo(() => (image ? URL.createObjectURL(image) : null), [image]);

  useEffect(() => {
    getAlumniProfile()
      .then(setProfile)
      .catch((e) => toast.error(errorMessage(e)));
  }, []);

  if (!profile) return <PageLoader />;
  const set = (k) => (e) => setProfile({ ...profile, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fields = {
        name: profile.name,
        phone_number: profile.phone_number,
        program: profile.program,
        batch: profile.batch,
        grade: profile.grade,
        current_role: profile.current_role,
        university: profile.university,
        city: profile.city,
        linkedin_url: profile.linkedin_url,
        tags: profile.tags || "",
        bio: profile.bio,
        show_contact: profile.show_contact,
      };
      setProfile(await updateAlumniProfile(fields, image));
      setImage(null);
      toast.success("Profile saved.");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const addWork = async () => {
    if (!work.position || !work.company) return;
    try {
      const item = await addWorkHistory({ ...work, year: Number(work.year) || THIS_YEAR });
      setProfile({ ...profile, work_histories: [item, ...profile.work_histories] });
      setWork({ year: String(THIS_YEAR), position: "", company: "" });
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_22rem]">
      <Card title="My profile" subtitle="This is what other alumni see in the directory.">
        <form onSubmit={save} className="space-y-5">
          <div className="flex items-center gap-4">
            {preview || profile.image_url ? (
              <img src={preview || profile.image_url} alt="" className="h-20 w-20 rounded-2xl object-cover" />
            ) : (
              <Initials name={profile.name} className="h-20 w-20 rounded-2xl text-2xl" />
            )}
            <FilePicker accept="image/*" icon={Camera} onFile={setImage}>
              Change photo
            </FilePicker>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <TextField id="name" label="Full name" value={profile.name} onChange={set("name")} />
            <TextField id="email" label="Email" value={profile.email} disabled />
            <TextField id="phone_number" label="Mobile" value={profile.phone_number} onChange={set("phone_number")} />
            <TextField id="batch" as="select" label="Class of" value={profile.batch} onChange={set("batch")}>
              {YEARS.map((y) => (
                <option key={y}>{y}</option>
              ))}
            </TextField>
            <TextField id="program" label="Programme" value={profile.program} onChange={set("program")} />
            <TextField id="grade" label="Last grade at TIS" value={profile.grade} onChange={set("grade")} />
            <TextField id="current_role" label="What you do now" value={profile.current_role} onChange={set("current_role")} />
            <TextField id="university" label="University" value={profile.university} onChange={set("university")} />
            <TextField id="city" label="City / country" value={profile.city} onChange={set("city")} />
            <TextField id="linkedin_url" label="LinkedIn" value={profile.linkedin_url} onChange={set("linkedin_url")} />
          </div>
          <TextField id="tags" label="Interests / skills" hint="Comma-separated" value={profile.tags || ""} onChange={set("tags")} />
          <TextField id="bio" as="textarea" rows={4} label="About you" value={profile.bio} onChange={set("bio")} />
          <Checkbox
            id="show_contact"
            checked={!!profile.show_contact}
            onChange={(e) => setProfile({ ...profile, show_contact: e.target.checked })}
            label="Show my email and phone to other alumni"
            description="Only signed-in, approved TIS alumni can see the directory."
          />
          <Button type="submit" loading={saving} icon={Save}>
            Save profile
          </Button>
        </form>
      </Card>
      <Card title="Work history">
        <div className="space-y-3">
          {profile.work_histories.map((w) => (
            <div key={w.id} className="flex items-start justify-between gap-3 rounded-xl bg-slate-50 p-3 text-sm">
              <div>
                <p className="font-semibold text-slate-900">{w.position}</p>
                <p className="text-slate-600">
                  {w.company} &middot; {w.year}
                </p>
              </div>
              <button
                type="button"
                onClick={async () => {
                  await deleteWorkHistory(w.id).catch(() => {});
                  setProfile({ ...profile, work_histories: profile.work_histories.filter((x) => x.id !== w.id) });
                }}
                className="text-slate-400 hover:text-red-600"
                aria-label="Remove"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          <div className="space-y-2 rounded-xl border border-dashed border-slate-300 p-3">
            <input className="w-full rounded-lg border-slate-300 text-sm" placeholder="Position" value={work.position} onChange={(e) => setWork({ ...work, position: e.target.value })} />
            <input className="w-full rounded-lg border-slate-300 text-sm" placeholder="Company" value={work.company} onChange={(e) => setWork({ ...work, company: e.target.value })} />
            <div className="flex gap-2">
              <input className="w-24 rounded-lg border-slate-300 text-sm" placeholder="Year" value={work.year} onChange={(e) => setWork({ ...work, year: e.target.value })} />
              <Button type="button" size="sm" icon={Plus} onClick={addWork} disabled={!work.position || !work.company}>
                Add
              </Button>
            </div>
          </div>
          <p className="text-xs text-slate-500">Member since {formatDate(profile.created_at) || "registration"}.</p>
        </div>
      </Card>
    </div>
  );
}
